import { useEffect, useState } from "react";
import {
  useImageStore,
  ThumbnailSize,
} from "entity-store/image-store/use-image-store";
import { ProjectTemplate } from "PosterEditor/model/project-template";
import { useSizePresets } from "PosterEditor/model/size-presets/size-preset";
import { aspectFitSize } from "PosterEditor/utils/rect-utils";
import { PosterProject } from "PosterEditor/model/poster-project";
import { Size } from "PosterEditor/model/poster-types";

interface UseProjectThumbnailProps {
  template?: ProjectTemplate;
  project?: PosterProject;
  //Desired size - will be aspect-fit to this size,
  //and actual size will be returned.
  size: Size;
  fallbackToMapImage: Boolean;
}
export const useProjectThumbnail = ({
  template,
  project,
  size,
  fallbackToMapImage = false,
}: UseProjectThumbnailProps) => {
  const [snapshot, setSnapshot] = useState<string | null>(null);
  const { getImageUrlWithSize, getRenderedThumbnailImageURL } = useImageStore();
  const { fullThumbnailSizeFromProject } = useSizePresets();

  useEffect(() => {
    let objectURL: string | null = null;

    if (template && template.thumbnail) {
      const fetchSnapshot = async () => {
        // Request pre-generated thumbnail (600px) - avoids Supabase transform limits
        const url = await getImageUrlWithSize(
          template.thumbnail,
          "png",
          true,
          undefined,
          "thumb-md" // 600px pre-generated thumbnail
        );
        objectURL = url;
        setSnapshot(url);
      };

      fetchSnapshot();

      return () => {
        if (objectURL) {
          URL.revokeObjectURL(objectURL);
        }
      };
    } else if (project) {
      const fetchSnapshot = async () => {
        const url = await getRenderedThumbnailImageURL(project.id);
        objectURL = url ?? null;
        setSnapshot(url ?? null);
      };

      fetchSnapshot();

      return () => {
        if (objectURL) {
          URL.revokeObjectURL(objectURL);
        }
      };
    }
  }, []);

  //Fallback to map style
  const styleURL = template?.project.mapStyle ?? project?.mapStyle;
  const mapStyle = styleURL?.replace(/^mapbox:\/\/styles\/jamesbkelly\//, "");
  const mapImageURL = `/pallete-components/map-thumbnails/${mapStyle}.webp`;

  let image = snapshot || template?.thumbnail;
  if (!image && fallbackToMapImage) {
    image = mapImageURL;
  }
  const proj = project ?? template?.project;

  if (!proj) {
    return { image: "", size: { width: 0, height: 0 } };
  }

  const thumbnailSize = fullThumbnailSizeFromProject(proj);

  const scaled = aspectFitSize(thumbnailSize, {
    width: size.width,
    height: size.height,
  });

  return { image, size: scaled };
};
